import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import {
  CONSTANTS,
  ERROR_EMAIL_NOT_VERIFIED,
  ERROR_WRONG_EMAIL_OR_PASSWORD,
} from '../constants/constants';
import {
  BehaviorSubject,
  Observable,
  iif,
  map,
  mergeMap,
  of,
  tap,
  throwError,
} from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Category } from '../types/event-response.type';
import { Organization } from '../types/organization.type';
import { EmailVerification } from '../types/email-verification.type';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public user: User | null = null;
  private _user = new BehaviorSubject<User | null>(null);
  public user$ = this._user.asObservable();
  public isUserLoaded: boolean = false;
  private userLoading: Observable<any> | null = null;

  constructor(private http: HttpClient) {
    iif(
      () => !this.tokenExpired(),
      this.getUser(),
      new Observable((subscriber) => {
        this.isUserLoaded = true;

        subscriber.next(null);
      })
    ).subscribe();
  }

  set jwt(value: string) {
    localStorage.setItem('jwt', value);
  }

  get jwt(): string {
    return localStorage.getItem('jwt') || '';
  }

  set expiresAt(value: string) {
    localStorage.setItem('expires_at', value);
  }

  get expiresAt(): string {
    return localStorage.getItem('expires_at') || '';
  }

  register(registerForm: RegisterForm): Observable<object> {
    return this.http.post(`${CONSTANTS.BASE_API}register`, registerForm);
  }

  login(loginForm: LoginForm): Observable<any> {
    return this.http.post(`${CONSTANTS.BASE_API}login`, loginForm).pipe(
      tap((res: any) => {
        this.jwt = res.access_token;
        this.expiresAt = res.expires_at;
      }),
      mergeMap(() => this.getUser(true)),
      catchError((err: HttpErrorResponse) => {
        if (err.error['error_code'] === ERROR_WRONG_EMAIL_OR_PASSWORD) {
          return of(ERROR_WRONG_EMAIL_OR_PASSWORD);
        }

        return throwError(() => err);
      })
    );
  }

  reset(): void {
    this.user = null;
    this.userBehaviourSubjectNext();
    this.jwt = '';
    this.expiresAt = '';
  }

  userBehaviourSubjectNext(): void {
    this._user.next(this.user);
  }

  logout(): Observable<any> {
    return this.http.get(`${CONSTANTS.BASE_API}logout`).pipe(
      tap(() => {
        this.reset();
      })
    );
  }

  forgotPassword(email: string): Observable<any> {
    return this.http.post(`${CONSTANTS.BASE_API}forgot-password`, {
      email,
    });
  }

  resetPassword(resetPasswordForm: ResetPasswordForm): Observable<object> {
    return this.http.post(
      `${CONSTANTS.BASE_API}reset-password`,
      resetPasswordForm
    );
  }

  getUser(forceReload: boolean = false): Observable<any> {
    if (!this.userLoading || forceReload) {
      this.userLoading = this.http.get(`${CONSTANTS.BASE_API}user`).pipe(
        tap((res: any) => {
          this.isUserLoaded = true;
          this.prepareUser(res);
          this.userBehaviourSubjectNext();
        }),
        catchError((err: HttpErrorResponse) => {
          if (err.error['message'] === ERROR_EMAIL_NOT_VERIFIED) {
            return of(ERROR_EMAIL_NOT_VERIFIED);
          }

          return throwError(() => err);
        })
      );
    } else if (this.isUserLoaded) {
      return this._user;
    }

    return this.userLoading;
  }

  tokenExpired(): boolean {
    return (
      !this.expiresAt ||
      new Date(this.expiresAt).valueOf() <= new Date().valueOf()
    );
  }

  prepareUser(user: User): void {
    this.user = {
      ...user,
      name: `${user.first_name} ${user.last_name}`,
    };
  }

  isAuthorized(): Observable<boolean> {
    return this.getUser().pipe(
      map((user) => {
        return Boolean(user);
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  updateSetting(setting: string, value: any): void {
    if (this.user) {
      this.user.settings[setting] = value;
      this.userBehaviourSubjectNext();
    }
  }

  confirmEmail(
    userId: string,
    hash: string,
    expires: string,
    signature: string
  ): Observable<EmailVerification> {
    const params = new HttpParams()
      .set('expires', expires)
      .set('signature', signature);
    return this.http.get<EmailVerification>(
      `${CONSTANTS.BASE_API}email/verify/${userId}/${hash}`,
      { params: params }
    );
  }
}
interface Follows {
  categories: Partial<Category>[];
  organizations: Partial<Organization>[];
}

export interface User {
  first_name: string;
  last_name: string;
  name?: string;
  email: string;
  id: number;
  is_active: boolean;
  permissions: any[];
  role: UserRole;
  avatar?: string;
  sex?: string;
  dob: string;
  country?: string | null;
  follows: Follows;
  phone_number?: string;
  settings: Record<string, string>;
}

interface UserRole {
  display_name: string;
  id: number;
  name: string;
}

export interface RegisterForm {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  password_confirmation: string;
  organization_name: string;
  organization_email: string;
  token: string;
}

export interface LoginForm {
  email: string;
  password: string;
}

export interface ResetPasswordForm {
  token: string;
  password: string;
  password_confirmation: string;
}
